/**
 * EPIC 56 — the one staffing arithmetic, shared by the server and every view.
 *
 * A trade (Job) states a NEED: how many people at the same time
 * (`workerCount`), over which period, on which daily shift. Assignments are
 * SEGMENTS — `[startAt, endAt)` stretches of one person's time. Everything a
 * reader wants to know is a question about those two:
 *
 *   - Is every shift covered?          → `planCoverage`
 *   - Does this change break a rule?   → `validateTransition`
 *   - What does the change produce?    → `applyOps`
 *   - Which days does this person work → `segmentWorkDays`
 *   - Which ordered place does it fill → `choosePlace` (M-D4a)
 *
 * Capacity limits CONCURRENT segments inside a shift, not the number of
 * names: Jaan Mon–Tue and Peeter Wed–Fri on a one-place order is correct; both
 * on Wednesday is not. Two half-days back to back fill one place.
 *
 * Pure and dependency-free apart from the work calendar, so Cloud Functions can
 * import it by relative path (they may not value-import the package) and the
 * browser through `@rendimees24/shared-types`.
 */

import type { JobShift } from './job';
import {
  calendarDate,
  DEFAULT_WORK_TIME_ZONE,
  localDayStart,
  nextCalendarDate,
  wallTime,
  wallTimeToInstant,
} from './workCalendar';

/** Inlined copy of `DEFAULT_SHIFT` — job.ts values must not be imported here. */
const FALLBACK_SHIFT: JobShift = {
  dailyStart: '08:00',
  dailyEnd: '16:30',
  workingDays: [1, 2, 3, 4, 5],
  breakMinutes: 30,
};

const MIN = 60_000;

// ── Shapes ───────────────────────────────────────────────────────────────────

export interface StaffingNeed {
  startAt: string;
  endAt: string;
  workerCount: number;
  shift?: JobShift | null;
}

export interface Segment {
  id: string;
  workerId: string;
  startAt: string;
  endAt: string;
  origin?: string;
  replacesAssignmentId?: string;
  /** M-D4a — the ordered place (`'p1'` …) this segment fills; immutable once set. */
  placeId?: string;
}

export interface ShiftOccurrence {
  /** Local date the shift starts on (site zone). */
  date: string;
  startAt: string;
  endAt: string;
  startMs: number;
  endMs: number;
}

export type DayCoverageState = 'covered' | 'partial' | 'uncovered' | 'over';

export interface DayCoverage extends ShiftOccurrence {
  required: number;
  /** Fewest people on at any moment of the shift. */
  covered: number;
  /** Most people on at any moment of the shift. */
  peak: number;
  /** required − covered when short, else 0. */
  shortBy: number;
  workerIds: string[];
  state: DayCoverageState;
}

export interface PlanCoverage {
  days: DayCoverage[];
  totalShifts: number;
  coveredShifts: number;
  /** Shifts with at least one moment short of the need. */
  shortShifts: number;
  /** Shifts with more people at once than places. */
  overShifts: number;
}

export type StaffingViolation =
  | { code: 'OVER_CAPACITY'; date: string; peak: number; required: number }
  | { code: 'WORKER_OVERLAP'; workerId: string; assignmentIds: [string, string] }
  | { code: 'OUTSIDE_ORDER'; assignmentId: string; workerId: string }
  | { code: 'BACKWARDS'; assignmentId: string; workerId: string }
  | { code: 'NO_SHIFT'; assignmentId: string; workerId: string }
  | { code: 'PAST_LOCKED'; workerId: string; lockedUntil: string }
  | { code: 'NOT_FOUND'; assignmentId: string }
  | { code: 'SAME_WORKER'; workerId: string }
  | { code: 'NOTHING_TO_CHANGE'; assignmentId?: string }
  /** A hard compliance/suitability block (right to work, A1, status, pay…). */
  | { code: 'NOT_ELIGIBLE'; workerId: string; reasons: string[] }
  /** The trade is completed / cancelled / invoiced, or its work has ended. */
  | { code: 'JOB_CLOSED'; jobId: string; status: string }
  /** Soft warnings the request did not accept. */
  | { code: 'WARNINGS_NOT_ACCEPTED'; workerId: string; codes: string[] }
  | {
      code: 'BOOKED_ELSEWHERE';
      workerId: string;
      jobId: string;
      startAt: string;
      endAt: string;
    }
  /**
   * M-D4a — no place is free for the whole window of a new segment (every
   * place has someone in part of it), so which place it fills cannot be
   * decided without guessing. The request must name one (`placeId`).
   */
  | { code: 'PLACE_AMBIGUOUS'; workerId: string; startAt: string; endAt: string; places: string[] }
  /** M-D4a — the place already has another segment at the same time. */
  | { code: 'PLACE_TAKEN'; workerId: string; placeId: string; assignmentId: string }
  /**
   * M-D4a — the named place is not one of this trade's places, or the
   * trade's places are not identified yet (rows from before place identity).
   */
  | { code: 'PLACE_UNKNOWN'; workerId: string; placeId: string }
  /**
   * M-D4b v4.3 — the change cuts today's shift of someone already at work and
   * leaves ordered minutes uncovered: say whether the client ended it
   * (`client_ended`) or it is RM24's shortfall (`provider_shortfall`).
   */
  | { code: 'SHIFT_END_REASON_REQUIRED'; workerId: string; at: string };

export type PlanOp =
  | {
      op: 'add';
      workerId: string;
      startAt: string;
      endAt: string;
      origin?: string;
      replacesAssignmentId?: string;
      /** M-D4a — the place the new segment fills; absent = the automatic rule. */
      placeId?: string;
    }
  | { op: 'setWindow'; assignmentId: string; startAt: string; endAt: string }
  /**
   * M-D4a — the segment's person continues on `placeId` from `from` to the
   * segment's end: the segment is cut at `from`, a `place_moved` segment
   * takes the rest on the new place.
   */
  | { op: 'movePlace'; assignmentId: string; from: string; placeId: string }
  /** Take `[from, until)` out of one segment; `until` absent = to its end. */
  | { op: 'release'; assignmentId: string; from: string; until?: string | null }
  /** Take `[from, until)` out of every segment the person has on this trade. */
  | { op: 'releaseWorker'; workerId: string; from: string; until: string }
  | {
      op: 'replace';
      assignmentId: string;
      replacementWorkerId: string;
      from: string;
      until?: string | null;
    };

export interface ApplyResult {
  segments: Segment[];
  createdIds: string[];
  removedIds: string[];
  changedIds: string[];
  errors: StaffingViolation[];
}

export interface Hold {
  jobId: string;
  startAt: string;
  endAt: string;
}

interface Interval {
  start: number;
  end: number;
}

// ── Reading a job ────────────────────────────────────────────────────────────

/** Id readers use for an assignment written before segments had ids. */
export function legacyAssignmentId(workerId: string, index = 0): string {
  return index === 0 ? `legacy:${workerId}` : `legacy:${workerId}:${index}`;
}

interface JobLike {
  requirements?: {
    startAt?: string;
    endAt?: string;
    workerCount?: number;
    shift?: JobShift | null;
  } | null;
  assignments?: Array<{
    id?: string;
    workerId: string;
    startAt?: string;
    endAt?: string;
    origin?: string;
    replacesAssignmentId?: string;
    placeId?: string;
  }> | null;
  assignedWorkerIds?: string[] | null;
  staffingModel?: number | null;
}

export function needOf(job: JobLike): StaffingNeed {
  return {
    startAt: job.requirements?.startAt ?? '',
    endAt: job.requirements?.endAt ?? '',
    workerCount: Math.max(1, job.requirements?.workerCount ?? 1),
    shift: job.requirements?.shift ?? null,
  };
}

/**
 * The job's segments. A legacy row without an id gets `legacy:<workerId>`;
 * without a window it meant the order's whole window. A worker listed in
 * `assignedWorkerIds` with no row at all (old seeds) is read the same way.
 */
export function segmentsOf(job: JobLike): Segment[] {
  const req = job.requirements ?? {};
  const zone = req.shift?.timeZone ?? DEFAULT_WORK_TIME_ZONE;
  // A date-only bound means the whole local day (an end includes its day).
  // Read as an instant here so no reader drops the last day by parsing
  // "2026-10-30" as UTC midnight.
  const at = (v: string | undefined, isEnd: boolean): string => {
    if (!v) return '';
    if (!isDateOnly(v)) return v;
    const ms = boundMs(v, zone, isEnd);
    return Number.isFinite(ms) ? iso(ms) : '';
  };
  const seen = new Map<string, number>();
  const out: Segment[] = [];
  for (const a of job.assignments ?? []) {
    if (!a?.workerId) continue;
    const n = seen.get(a.workerId) ?? 0;
    seen.set(a.workerId, n + 1);
    out.push({
      id: a.id ?? legacyAssignmentId(a.workerId, n),
      workerId: a.workerId,
      startAt: at(a.startAt ?? req.startAt, false),
      endAt: at(a.endAt ?? req.endAt, true),
      ...(a.origin ? { origin: a.origin } : {}),
      ...(a.replacesAssignmentId ? { replacesAssignmentId: a.replacesAssignmentId } : {}),
      ...(a.placeId ? { placeId: a.placeId } : {}),
    });
  }
  // Before segments, `assignedWorkerIds` could name someone with no row (old
  // seeds), and that meant the whole order. On the segment model the list
  // also keeps people for their HISTORY only — reading those as booked for
  // the whole order would invent a plan, so only legacy jobs get this.
  if (job.staffingModel === 2) return out;
  for (const wid of job.assignedWorkerIds ?? []) {
    if (seen.has(wid)) continue;
    seen.set(wid, 1);
    out.push({
      id: legacyAssignmentId(wid),
      workerId: wid,
      startAt: at(req.startAt, false),
      endAt: at(req.endAt, true),
    });
  }
  return out;
}

/** Distinct people across segments — what `assignedWorkerIds` must equal. */
export function workerIdsOf(segments: Segment[]): string[] {
  return [...new Set(segments.map((s) => s.workerId))];
}

// ── Time ─────────────────────────────────────────────────────────────────────

function zoneOf(need: StaffingNeed): string {
  return need.shift?.timeZone ?? DEFAULT_WORK_TIME_ZONE;
}

function isDateOnly(v: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(v);
}

/**
 * An order bound as an instant. A date-only start is that local day's start; a
 * date-only END is the start of the following local day (the day is included).
 */
export function boundMs(value: string, zone: string, isEnd: boolean): number {
  if (!value) return NaN;
  if (isDateOnly(value)) {
    const day = isEnd ? nextCalendarDate(value, 1) : value;
    return Date.parse(localDayStart(day, zone) ?? `${day}T00:00:00Z`);
  }
  return Date.parse(value);
}

function iso(ms: number): string {
  return new Date(ms).toISOString();
}

function isoWeekday(ymd: string): number {
  const dow = new Date(`${ymd}T12:00:00Z`).getUTCDay();
  return dow === 0 ? 7 : dow;
}

/** Local wall time → instant; a DST gap/overlap falls back to the noon offset. */
function wallMs(local: string, zone: string): number {
  const exact = wallTimeToInstant(local, zone);
  if (exact) return Date.parse(exact);
  const noon = Date.parse(`${local.slice(0, 10)}T12:00:00Z`);
  const offset = Date.parse(`${wallTime(noon, zone)}:00Z`) - noon;
  return Date.parse(`${local}:00Z`) - offset;
}

/**
 * Every planned shift of the need, clipped to the order's own bounds, in the
 * site's zone. `range` (instants) clips further — for a board showing a week.
 */
const occurrenceCache = new Map<string, ShiftOccurrence[]>();

export function shiftOccurrences(
  need: StaffingNeed,
  range?: { startAt?: string; endAt?: string },
): ShiftOccurrence[] {
  // Every validation asks this many times over; the time-zone arithmetic is
  // the expensive part and the answer depends on these strings alone.
  const key = `${need.startAt}|${need.endAt}|${JSON.stringify(need.shift ?? null)}|${range?.startAt ?? ''}|${range?.endAt ?? ''}`;
  const hit = occurrenceCache.get(key);
  if (hit) return hit;
  const out = computeOccurrences(need, range);
  if (occurrenceCache.size > 500) occurrenceCache.clear();
  occurrenceCache.set(key, out);
  return out;
}

function computeOccurrences(
  need: StaffingNeed,
  range?: { startAt?: string; endAt?: string },
): ShiftOccurrence[] {
  const zone = zoneOf(need);
  const shift = need.shift && need.shift.workingDays?.length ? need.shift : FALLBACK_SHIFT;
  const lo = Math.max(
    boundMs(need.startAt, zone, false),
    range?.startAt ? Date.parse(range.startAt) : -Infinity,
  );
  const hi = Math.min(
    boundMs(need.endAt, zone, true),
    range?.endAt ? Date.parse(range.endAt) : Infinity,
  );
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || lo >= hi) return [];
  const working = new Set(shift.workingDays);
  const offset = shift.endDayOffset ?? 0;
  const out: ShiftOccurrence[] = [];
  // Start one day early: an overnight shift begun the evening before is in range.
  let day = nextCalendarDate(calendarDate(iso(lo), zone), -1);
  const last = calendarDate(iso(hi), zone);
  for (let guard = 0; day <= last && guard < 2000; guard += 1, day = nextCalendarDate(day, 1)) {
    if (!working.has(isoWeekday(day))) continue;
    const s = wallMs(`${day}T${shift.dailyStart}`, zone);
    const e = wallMs(`${nextCalendarDate(day, offset)}T${shift.dailyEnd}`, zone);
    const startMs = Math.max(s, lo);
    const endMs = Math.min(e, hi);
    if (!(endMs > startMs)) continue;
    out.push({ date: day, startAt: iso(startMs), endAt: iso(endMs), startMs, endMs });
  }
  return out;
}

function segInterval(s: Segment): Interval {
  return { start: Date.parse(s.startAt), end: Date.parse(s.endAt) };
}

function clip(i: Interval, lo: number, hi: number): Interval | null {
  const start = Math.max(i.start, lo);
  const end = Math.min(i.end, hi);
  return end > start ? { start, end } : null;
}

function mergeAll(list: Interval[]): Interval[] {
  const sorted = list.filter((v) => v.end > v.start).sort((a, b) => a.start - b.start);
  const out: Interval[] = [];
  for (const v of sorted) {
    const last = out[out.length - 1];
    if (last && v.start <= last.end) last.end = Math.max(last.end, v.end);
    else out.push({ ...v });
  }
  return out;
}

function subtract(a: Interval[], b: Interval[]): Interval[] {
  let result = mergeAll(a);
  for (const cut of mergeAll(b)) {
    result = result.flatMap((v) => {
      if (cut.end <= v.start || cut.start >= v.end) return [v];
      return [
        { start: v.start, end: Math.min(v.end, cut.start) },
        { start: Math.max(v.start, cut.end), end: v.end },
      ].filter((p) => p.end > p.start);
    });
  }
  return result;
}

/** Fewest and most concurrent intervals at any moment of `[lo, hi)`. */
function concurrency(intervals: Interval[], lo: number, hi: number): { min: number; max: number } {
  const events: Array<[number, number]> = [];
  for (const i of intervals) {
    const c = clip(i, lo, hi);
    if (!c) continue;
    events.push([c.start, 1], [c.end, -1]);
  }
  // Ends before starts at the same instant: 12:00 end and 12:00 start do not overlap.
  events.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  let cur = 0;
  let max = 0;
  let min = Infinity;
  let at = lo;
  for (const [t, d] of events) {
    if (t > at) {
      min = Math.min(min, cur);
      at = t;
    }
    cur += d;
    max = Math.max(max, cur);
  }
  if (hi > at) min = Math.min(min, cur);
  return { min: min === Infinity ? 0 : min, max };
}

// ── Coverage ─────────────────────────────────────────────────────────────────

export function occurrenceCoverage(
  occ: ShiftOccurrence,
  segments: Segment[],
  required: number,
): DayCoverage {
  const on = segments.filter((s) => clip(segInterval(s), occ.startMs, occ.endMs));
  const { min, max } = concurrency(on.map(segInterval), occ.startMs, occ.endMs);
  const state: DayCoverageState =
    max > required ? 'over' : min >= required ? 'covered' : max === 0 ? 'uncovered' : 'partial';
  return {
    ...occ,
    required,
    covered: min,
    peak: max,
    shortBy: Math.max(0, required - min),
    workerIds: workerIdsOf(on),
    state,
  };
}

export function planCoverage(
  need: StaffingNeed,
  segments: Segment[],
  range?: { startAt?: string; endAt?: string },
): PlanCoverage {
  const days = shiftOccurrences(need, range).map((o) =>
    occurrenceCoverage(o, segments, need.workerCount),
  );
  return {
    days,
    totalShifts: days.length,
    coveredShifts: days.filter((d) => d.covered >= d.required).length,
    shortShifts: days.filter((d) => d.covered < d.required).length,
    overShifts: days.filter((d) => d.peak > d.required).length,
  };
}

/** Coverage of the job as stored — the reader's one-liner. */
export function jobCoverage(job: JobLike, range?: { startAt?: string; endAt?: string }): PlanCoverage {
  return planCoverage(needOf(job), segmentsOf(job), range);
}

/** The shifts one segment works, i.e. this person's planned days on this trade. */
export function segmentWorkDays(need: StaffingNeed, segment: Segment): ShiftOccurrence[] {
  const i = segInterval(segment);
  return shiftOccurrences(need).flatMap((o) => {
    const c = clip(i, o.startMs, o.endMs);
    return c ? [{ ...o, startAt: iso(c.start), endAt: iso(c.end), startMs: c.start, endMs: c.end }] : [];
  });
}

/** One person's planned shifts on the job, across all their segments. */
export function workerWorkDays(job: JobLike, workerId: string): ShiftOccurrence[] {
  const need = needOf(job);
  return segmentsOf(job)
    .filter((s) => s.workerId === workerId)
    .flatMap((s) => segmentWorkDays(need, s))
    .sort((a, b) => a.startMs - b.startMs);
}

/** True when the person has a segment running at `atIso` or on that local day's shift. */
export function workerPlannedOn(job: JobLike, workerId: string, localDate: string): boolean {
  return workerWorkDays(job, workerId).some((d) => d.date === localDate);
}

/**
 * The instant a choice "from <date>" means: that day's shift start, or the
 * local day's start when the date is not a working day.
 */
export function dayBoundary(need: StaffingNeed, date: string): string {
  const zone = zoneOf(need);
  const occ = shiftOccurrences(need).find((o) => o.date === date);
  if (occ) return occ.startAt;
  return localDayStart(date, zone) ?? `${date}T00:00:00.000Z`;
}

/** The window of whole shifts from `fromDate` to `toDate` inclusive. */
export function daysWindow(
  need: StaffingNeed,
  fromDate: string,
  toDate: string,
): { startAt: string; endAt: string } | null {
  const occ = shiftOccurrences(need).filter((o) => o.date >= fromDate && o.date <= toDate);
  if (occ.length === 0) return null;
  return { startAt: occ[0].startAt, endAt: occ[occ.length - 1].endAt };
}

// ── Changes ──────────────────────────────────────────────────────────────────

/**
 * Apply the ops in order. Unknown ids and self-replacement are reported as
 * errors and the op skipped; the caller refuses the whole change when any
 * error or violation comes back — nothing is ever half-applied.
 *
 * Places (M-D4a): a replacement and the tail of a split carry the place of
 * the segment they came from (`cut` copies the segment). With `opts.places`
 * (the trade's `workerCount`), an `add` gets its place from `choosePlace` —
 * the place the op names, else the lowest free one, else `PLACE_AMBIGUOUS`.
 * On a trade whose segments are not all place-identified yet (legacy rows),
 * no place is assigned (the migration does that) and a named one is
 * `PLACE_UNKNOWN`. Without `opts.places` places are left as they are.
 */
export function applyOps(
  before: Segment[],
  ops: PlanOp[],
  newId: () => string,
  opts: { places?: number } = {},
): ApplyResult {
  let segs = before.map((s) => ({ ...s }));
  const created: string[] = [];
  const errors: StaffingViolation[] = [];
  const identified = placesIdentified(before);

  const cut = (seg: Segment, fromMs: number, untilMs: number): Segment[] => {
    const i = segInterval(seg);
    const f = Math.max(fromMs, i.start);
    const u = Math.min(untilMs, i.end);
    if (!(u > f)) return [seg];
    const parts: Segment[] = [];
    if (f > i.start) parts.push({ ...seg, endAt: iso(f) });
    if (i.end > u) {
      if (parts.length === 0) parts.push({ ...seg, startAt: iso(u) });
      else {
        const id = newId();
        created.push(id);
        parts.push({ ...seg, id, startAt: iso(u), origin: 'split', replacesAssignmentId: seg.id });
      }
    }
    return parts;
  };

  for (const op of ops) {
    if (op.op === 'add') {
      const id = newId();
      created.push(id);
      let placeId: string | undefined;
      if (opts.places !== undefined) {
        if (!identified) {
          if (op.placeId) errors.push({ code: 'PLACE_UNKNOWN', workerId: op.workerId, placeId: op.placeId });
        } else {
          const pick = choosePlace(segs, opts.places, op);
          if ('error' in pick) errors.push(pick.error);
          else placeId = pick.placeId;
        }
      }
      segs.push({
        id,
        workerId: op.workerId,
        startAt: op.startAt,
        endAt: op.endAt,
        origin: op.origin ?? 'assigned',
        ...(op.replacesAssignmentId ? { replacesAssignmentId: op.replacesAssignmentId } : {}),
        ...(placeId ? { placeId } : {}),
      });
      continue;
    }
    if (op.op === 'releaseWorker') {
      const f = Date.parse(op.from);
      const u = Date.parse(op.until);
      segs = segs.flatMap((s) => (s.workerId === op.workerId ? cut(s, f, u) : [s]));
      continue;
    }
    const target = segs.find((s) => s.id === op.assignmentId);
    if (!target) {
      errors.push({ code: 'NOT_FOUND', assignmentId: op.assignmentId });
      continue;
    }
    if (op.op === 'movePlace') {
      const iv = segInterval(target);
      const f = Math.max(Date.parse(op.from), iv.start);
      if (!(iv.end > f) || target.placeId === op.placeId) {
        errors.push({ code: 'NOTHING_TO_CHANGE', assignmentId: target.id });
        continue;
      }
      if (!identified || !placesOf(opts.places ?? 1, segs).includes(op.placeId)) {
        errors.push({ code: 'PLACE_UNKNOWN', workerId: target.workerId, placeId: op.placeId });
        continue;
      }
      segs = segs.flatMap((s) => (s.id === target.id ? cut(s, f, iv.end) : [s]));
      const id = newId();
      created.push(id);
      segs.push({
        id,
        workerId: target.workerId,
        startAt: iso(f),
        endAt: target.endAt,
        origin: 'place_moved',
        replacesAssignmentId: target.id,
        placeId: op.placeId,
      });
      continue;
    }
    if (op.op === 'setWindow') {
      if (target.startAt === op.startAt && target.endAt === op.endAt) {
        errors.push({ code: 'NOTHING_TO_CHANGE', assignmentId: target.id });
        continue;
      }
      segs = segs.map((s) => (s.id === target.id ? { ...s, startAt: op.startAt, endAt: op.endAt } : s));
      continue;
    }
    const i = segInterval(target);
    const f = Math.max(Date.parse(op.from), i.start);
    const u = Math.min(op.until ? Date.parse(op.until) : i.end, i.end);
    if (!(u > f)) {
      errors.push({ code: 'NOTHING_TO_CHANGE', assignmentId: target.id });
      continue;
    }
    if (op.op === 'replace' && op.replacementWorkerId === target.workerId) {
      errors.push({ code: 'SAME_WORKER', workerId: target.workerId });
      continue;
    }
    segs = segs.flatMap((s) => (s.id === target.id ? cut(s, f, u) : [s]));
    if (op.op === 'replace') {
      const id = newId();
      created.push(id);
      segs.push({
        id,
        workerId: op.replacementWorkerId,
        startAt: iso(f),
        endAt: iso(u),
        origin: 'replacement',
        replacesAssignmentId: target.id,
        // The replacement fills the same place (M-D4a).
        ...(target.placeId ? { placeId: target.placeId } : {}),
      });
    }
  }

  const afterIds = new Set(segs.map((s) => s.id));
  const removedIds = before.filter((s) => !afterIds.has(s.id)).map((s) => s.id);
  const createdLive = created.filter((id) => afterIds.has(id));
  const beforeById = new Map(before.map((s) => [s.id, s]));
  const changedIds = segs
    .filter((s) => {
      const b = beforeById.get(s.id);
      return b && (b.startAt !== s.startAt || b.endAt !== s.endAt);
    })
    .map((s) => s.id);
  return { segments: segs, createdIds: createdLive, removedIds, changedIds, errors };
}

// ── Places (M-D4a) ───────────────────────────────────────────────────────────

/** `'p3'` → 3; anything else → NaN. */
export function placeNumber(placeId: string): number {
  const m = /^p(\d+)$/.exec(placeId);
  return m ? Number(m[1]) : NaN;
}

function byPlaceNumber(a: string, b: string): number {
  return placeNumber(a) - placeNumber(b) || a.localeCompare(b);
}

/** The ordered places `'p1'` … `'pN'`. */
export function orderedPlaces(workerCount: number): string[] {
  return Array.from({ length: Math.max(1, Math.floor(workerCount)) }, (_, i) => `p${i + 1}`);
}

/**
 * Every place of the trade: the ordered ones plus any a segment already
 * holds — lowering `workerCount` never takes a used place away.
 */
export function placesOf(workerCount: number, segments: readonly Segment[]): string[] {
  const set = new Set(orderedPlaces(workerCount));
  for (const s of segments) if (s.placeId) set.add(s.placeId);
  return [...set].sort(byPlaceNumber);
}

/**
 * True when every segment names its place — the trade is on the place model.
 * A trade with no segment at all is (nothing is unknown). One legacy row
 * without a place makes the whole trade unidentified: which place it holds is
 * unknown, so no other place can be called free.
 */
export function placesIdentified(segments: readonly Segment[]): boolean {
  return segments.every((s) => !!s.placeId);
}

function overlaps(s: Pick<Segment, 'startAt' | 'endAt'>, lo: number, hi: number): boolean {
  return Date.parse(s.startAt) < hi && lo < Date.parse(s.endAt);
}

/**
 * Ordered places with no segment anywhere in `[startAt, endAt)`, lowest
 * first. Only the ordered `p1`…`pN` are offered: a place left over from a
 * higher `workerCount` keeps its history but is not filled automatically.
 */
export function freePlaces(
  segments: readonly Segment[],
  workerCount: number,
  startAt: string,
  endAt: string,
): string[] {
  const lo = Date.parse(startAt);
  const hi = Date.parse(endAt);
  return orderedPlaces(workerCount).filter(
    (p) => !segments.some((s) => s.placeId === p && overlaps(s, lo, hi)),
  );
}

/**
 * The place a new segment fills.
 *
 *   - Named in the request → that place, if it is one of the trade's places
 *     (a clash with someone already there is judged by `validateTransition`
 *     as `PLACE_TAKEN`, like any other change);
 *   - otherwise the LOWEST ordered place with no segment overlapping the new
 *     window;
 *   - otherwise `PLACE_AMBIGUOUS` — every place has someone in part of the
 *     window, and picking one would be a guess about whose place this is.
 */
export function choosePlace(
  segments: readonly Segment[],
  workerCount: number,
  add: { workerId: string; startAt: string; endAt: string; placeId?: string },
): { placeId: string } | { error: StaffingViolation } {
  const all = placesOf(workerCount, segments);
  if (add.placeId) {
    if (!all.includes(add.placeId)) {
      return { error: { code: 'PLACE_UNKNOWN', workerId: add.workerId, placeId: add.placeId } };
    }
    return { placeId: add.placeId };
  }
  const free = freePlaces(segments, workerCount, add.startAt, add.endAt);
  if (free.length > 0) return { placeId: free[0] };
  return {
    error: {
      code: 'PLACE_AMBIGUOUS',
      workerId: add.workerId,
      startAt: add.startAt,
      endAt: add.endAt,
      places: all,
    },
  };
}

/**
 * Two segments of one place at the same moment — a place is one person at a
 * time. Typical cause: a person's window extended over time their place has
 * given to someone else; the way through is to add them for that time on
 * another place (the message says so). Judged for segments the change created or moved (`changedIds`), as
 * `WORKER_OVERLAP` is: an untouched legacy clash does not block an unrelated
 * edit.
 */
export function placeCollisions(after: readonly Segment[], changedIds: ReadonlySet<string>): StaffingViolation[] {
  const out: StaffingViolation[] = [];
  for (const s of after) {
    if (!s.placeId || !changedIds.has(s.id)) continue;
    const lo = Date.parse(s.startAt);
    const hi = Date.parse(s.endAt);
    const other = after.find((o) => o.id !== s.id && o.placeId === s.placeId && overlaps(o, lo, hi));
    if (other) out.push({ code: 'PLACE_TAKEN', workerId: s.workerId, placeId: s.placeId, assignmentId: other.id });
  }
  return out;
}

/** Per person: time the change ADDS to their plan on this trade. */
export function grownTime(before: Segment[], after: Segment[]): Map<string, Interval[]> {
  const out = new Map<string, Interval[]>();
  for (const wid of workerIdsOf(after)) {
    const a = after.filter((s) => s.workerId === wid).map(segInterval);
    const b = before.filter((s) => s.workerId === wid).map(segInterval);
    const grown = subtract(a, b);
    if (grown.length) out.set(wid, grown);
  }
  return out;
}

/**
 * Per person: time whose PLACE the change moves — on a place before and not
 * after, or the other way round (M-D4a). A move to another place keeps the
 * person's time the same, so `changedTime` alone would not see it; the place
 * of time already worked is as fixed as the time itself.
 */
export function changedPlaceTime(before: Segment[], after: Segment[]): Map<string, Interval[]> {
  const out = new Map<string, Interval[]>();
  for (const wid of workerIdsOf([...before, ...after])) {
    const keys = new Set([...before, ...after].filter((s) => s.workerId === wid).map((s) => s.placeId ?? ''));
    const diff: Interval[] = [];
    for (const k of keys) {
      const a = after.filter((s) => s.workerId === wid && (s.placeId ?? '') === k).map(segInterval);
      const b = before.filter((s) => s.workerId === wid && (s.placeId ?? '') === k).map(segInterval);
      diff.push(...subtract(a, b), ...subtract(b, a));
    }
    const merged = mergeAll(diff);
    if (merged.length) out.set(wid, merged);
  }
  return out;
}

/** Per person: time the change adds OR removes. */
function changedTime(before: Segment[], after: Segment[]): Map<string, Interval[]> {
  const out = new Map<string, Interval[]>();
  for (const wid of workerIdsOf([...before, ...after])) {
    const a = after.filter((s) => s.workerId === wid).map(segInterval);
    const b = before.filter((s) => s.workerId === wid).map(segInterval);
    const diff = mergeAll([...subtract(a, b), ...subtract(b, a)]);
    if (diff.length) out.set(wid, diff);
  }
  return out;
}

export interface ValidateOptions {
  /**
   * Per person, the instant before which their plan on this trade may not
   * change: the start of today, or later when work is already recorded.
   */
  lockBefore: (workerId: string) => number;
  /**
   * M-D4b v4.2 B1(b) — on an order billed per place and shift, coverage is a
   * money signal: none may be ADDED for time before this instant (now). Cutting
   * keeps the ordinary lock. Corrections after the fact go through
   * `setTimesheetPlace` (it bills work done, not held time).
   */
  addedCoverageLockBefore?: number;
}

/**
 * Every rule a staffing change must keep, judged on the result.
 *
 * Rules about a segment's own shape (inside the order, forwards, on a working
 * shift, not overlapping the same person) apply to segments the change
 * created or moved — an untouched legacy row does not block an unrelated
 * edit. Capacity is judged per shift and may not get WORSE: a legacy order
 * already over its places can be cleaned up, never filled further.
 */
export function validateTransition(
  need: StaffingNeed,
  before: Segment[],
  after: Segment[],
  opts: ValidateOptions,
): StaffingViolation[] {
  const out: StaffingViolation[] = [];
  const zone = zoneOf(need);
  const lo = boundMs(need.startAt, zone, false);
  const hi = boundMs(need.endAt, zone, true);
  const beforeById = new Map(before.map((s) => [s.id, s]));
  const changed = after.filter((s) => {
    const b = beforeById.get(s.id);
    return !b || b.startAt !== s.startAt || b.endAt !== s.endAt || b.workerId !== s.workerId;
  });

  for (const s of changed) {
    const i = segInterval(s);
    if (!(i.end > i.start)) {
      out.push({ code: 'BACKWARDS', assignmentId: s.id, workerId: s.workerId });
      continue;
    }
    if (i.start < lo || i.end > hi) {
      out.push({ code: 'OUTSIDE_ORDER', assignmentId: s.id, workerId: s.workerId });
      continue;
    }
    if (segmentWorkDays(need, s).length === 0) {
      out.push({ code: 'NO_SHIFT', assignmentId: s.id, workerId: s.workerId });
    }
  }

  const changedIds = new Set(changed.map((s) => s.id));
  for (const wid of workerIdsOf(after)) {
    const mine = after.filter((s) => s.workerId === wid).sort((a, b) => a.startAt.localeCompare(b.startAt));
    for (let i = 0; i < mine.length; i += 1) {
      for (let j = i + 1; j < mine.length; j += 1) {
        const a = segInterval(mine[i]);
        const b = segInterval(mine[j]);
        if (a.start < b.end && b.start < a.end && (changedIds.has(mine[i].id) || changedIds.has(mine[j].id))) {
          out.push({ code: 'WORKER_OVERLAP', workerId: wid, assignmentIds: [mine[i].id, mine[j].id] });
        }
      }
    }
  }

  out.push(...placeCollisions(after, changedIds));

  const timeDiff = changedTime(before, after);
  const placeDiff = changedPlaceTime(before, after);
  for (const wid of new Set([...timeDiff.keys(), ...placeDiff.keys()])) {
    const lock = opts.lockBefore(wid);
    const diff = [...(timeDiff.get(wid) ?? []), ...(placeDiff.get(wid) ?? [])];
    if (diff.some((d) => d.start < lock)) {
      out.push({ code: 'PAST_LOCKED', workerId: wid, lockedUntil: iso(lock) });
    }
  }

  if (opts.addedCoverageLockBefore !== undefined) {
    const lock = opts.addedCoverageLockBefore;
    for (const wid of workerIdsOf(after)) {
      const places = new Set(after.filter((s) => s.workerId === wid).map((s) => s.placeId ?? ''));
      const added: Interval[] = [];
      for (const p of places) {
        const a = after.filter((s) => s.workerId === wid && (s.placeId ?? '') === p).map(segInterval);
        const b = before.filter((s) => s.workerId === wid && (s.placeId ?? '') === p).map(segInterval);
        added.push(...subtract(a, b));
      }
      if (added.some((d) => d.start < lock) && !out.some((v) => v.code === 'PAST_LOCKED' && v.workerId === wid))
        out.push({ code: 'PAST_LOCKED', workerId: wid, lockedUntil: iso(lock) });
    }
  }

  const occ = shiftOccurrences(need);
  for (const o of occ) {
    const b = occurrenceCoverage(o, before, need.workerCount);
    const a = occurrenceCoverage(o, after, need.workerCount);
    if (a.peak > need.workerCount && a.peak > b.peak) {
      out.push({ code: 'OVER_CAPACITY', date: o.date, peak: a.peak, required: need.workerCount });
    }
  }
  return out;
}

/** Holds elsewhere that collide with time the change ADDS to someone. */
export function bookingConflicts(
  grown: Map<string, Interval[]>,
  holds: Map<string, Hold[]>,
): StaffingViolation[] {
  const out: StaffingViolation[] = [];
  for (const [wid, list] of grown) {
    for (const h of holds.get(wid) ?? []) {
      const hi = { start: Date.parse(h.startAt), end: Date.parse(h.endAt) };
      if (list.some((g) => g.start < hi.end && hi.start < g.end)) {
        out.push({ code: 'BOOKED_ELSEWHERE', workerId: wid, jobId: h.jobId, startAt: h.startAt, endAt: h.endAt });
      }
    }
  }
  return out;
}

/** The start of `date`'s local day in the need's zone, as ms. */
export function localDayStartMs(need: StaffingNeed, date: string): number {
  return Date.parse(localDayStart(date, zoneOf(need)) ?? `${date}T00:00:00Z`);
}

/** Today's local date in the need's zone. */
export function needToday(need: StaffingNeed, now: number | string | Date = Date.now()): string {
  return wallTime(now, zoneOf(need)).slice(0, 10);
}

export const STAFFING_MINUTE = MIN;
